import React, { useState, useEffect } from "react";
import {
  Checkbox,
  Tabs,
  Tab,
  Box,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  FormControlLabel
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import { getVal, setBool } from "../../helpers/process_morningstar.js";

function TableTabs({ selectedTable, setSelectedTable, tableComponents, setTablePeriod }) {
  const [periodAnchorEl, setPeriodAnchorEl] = useState(null);
  const [intangiblesAnchorEl, setIntangiblesAnchorEl] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("Annual"); // default period
  const [capRD, setCapRD] = useState(getVal("capRD"));
  const [includeOtherIncome, setIncludeOtherIncome] = useState(getVal("includeOtherIncome"));
  const periodMenuOpen = Boolean(periodAnchorEl);
  const intangiblesMenuOpen = Boolean(intangiblesAnchorEl);

  const handleMenuOpen = (event) => setPeriodAnchorEl(event.currentTarget);
  const handleMenuClose = () => setPeriodAnchorEl(null);

  const handleIntangiblesMenuOpen = (event) => setIntangiblesAnchorEl(event.currentTarget);
  const handleIntangiblesMenuClose = () => setIntangiblesAnchorEl(null);

  const handlePeriodSelect = (period) => {
    setSelectedPeriod(period);
    setTablePeriod(period); // call parent function to update data
    handleMenuClose();
  };

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "capRD") setCapRD(JSON.parse(event.newValue));
      if (event.key === "includeOtherIncome") setIncludeOtherIncome(JSON.parse(event.newValue));
    };

    const handleCustomUpdate = () => {
      setCapRD(getVal("capRD"));
      setIncludeOtherIncome(getVal("includeOtherIncome"));
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("localStorageUpdated", handleCustomUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("localStorageUpdated", handleCustomUpdate);
    };
  }, []);


  // 3️⃣ Handle checkbox toggle
  const handleCapRDChange = (event) => {
    const newValue = event.target.checked;
    setBool("capRD", newValue);
    setCapRD(newValue);

    window.dispatchEvent(new Event("localStorageUpdated"));

    handleMenuClose();
  };

  const handleIncludeOtherIncomeChange = (e) => {
    setBool("includeOtherIncome", !includeOtherIncome);
    setIncludeOtherIncome(!includeOtherIncome);

    window.dispatchEvent(new Event("localStorageUpdated"));

    handleIntangiblesMenuClose();
  };

  return (
    <Box sx={{ 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "stretch",
        width: '100%',
      }}
    >
      <Tabs
        value={selectedTable}
        onChange={(e, newValue) => setSelectedTable(newValue)}
        textColor="#324052"
        indicatorColor="#324052"
        orientation="vertical"
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          "& .MuiTab-root": {
            textTransform: "none",
            fontSize: 14,
            color: "#000000ff",
            minHeight: 30,
          },
          "& .Mui-selected": {
            color: "#324052",
            borderRadius: "8px",
            fontWeight: "bold",
          },
        }}
      >
        {tableComponents.map((t) => (
          <Tab
            key={t.tableName}
            value={t.tableName}
            label={
              <Box
                sx={{
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <span>{t.tableName}</span>
              </Box>
            }
            sx={{
              width: "100%",
              textAlign: "left",
              padding: "4px 6px",
              height: 25,
            }}
          />
        ))}
      </Tabs>

      <IconButton
        id="period-button"
        aria-controls={periodMenuOpen ? 'period-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={periodMenuOpen ? 'true' : undefined}
        onClick={handleMenuOpen}
        sx={{ textTransform: "none", 
          border: "1px solid #ccc", 
          mt: 2, 
          mx: 2,
          width: 180, 
          padding: '4px',
          justifyContent: "space-between", 
          borderRadius: 0,
          fontSize: 14,
          color: "#000000ff",
          alignItems: "flex-start",
        }}
      >
        {selectedPeriod}
        <ArrowDropDownIcon fontSize="small" />
      </IconButton>
      <Menu
        id="period-menu"
        anchorEl={periodAnchorEl}
        open={periodMenuOpen}
        onClose={handleMenuClose}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
          },
        }}
      >
        <MenuItem onClick={() => handlePeriodSelect("Annual")}>Annual</MenuItem>
        <MenuItem onClick={() => handlePeriodSelect("Quarterly")}>Quarterly</MenuItem>
      </Menu>
      <IconButton
        id="intangibles-button"
        aria-controls={intangiblesMenuOpen ? 'intangibles-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={intangiblesMenuOpen ? 'true' : undefined}
        onClick={handleIntangiblesMenuOpen}
        sx={{ textTransform: "none", 
          border: "1px solid #ccc", 
          mx: 2,
          width: 180, 
          padding: '4px',
          justifyContent: "space-between", 
          borderRadius: 0,
          fontSize: 14,
          color: "#000000ff",
          alignItems: "flex-start",
        }}
      >
        {getVal("includeOtherIncome") ? "Incl Intangibles" : "Excl Intangibles"}
        <ArrowDropDownIcon fontSize="small" />
      </IconButton>
      <Menu
        id="intangibles-menu"
        anchorEl={intangiblesAnchorEl}
        open={intangiblesMenuOpen}
        onClose={handleIntangiblesMenuClose}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
          },
        }}
      >
        <MenuItem onClick={handleIncludeOtherIncomeChange}>Excl Intangibles</MenuItem>
        <MenuItem onClick={handleIncludeOtherIncomeChange}>Incl Intangibles</MenuItem>
      </Menu>
     <FormControlLabel control={<Checkbox 
      defaultChecked 
        checked={capRD}
        onChange={handleCapRDChange}
        sx={{ 
          marginLeft: 2,
          '& .MuiSvgIcon-root': { fontSize: 16 } 
        }}
      />} 
      label="Cap R&D" 
        sx={{
          "& .MuiFormControlLabel-label": {
            fontSize: 12, // 👈 change this to whatever size you want
            fontWeight: "bold", 
          },
        }}
      />
    </Box>
  );
}

export default TableTabs;
